import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getOverview, type Overview, type OverviewFilters } from "../../services/overview-api";
import { getAppCatalog, type AppCatalog } from "../../services/app-catalog-api";
import { listCompanies, type Company } from "../../services/companies-api";

/** A single headline number. Kept deliberately plain — this page answers "where does the whole
 * platform stand right now", so the numbers should be readable at a glance, not decorated. */
function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded border border-surface-alt p-4">
      <p className="text-sm text-body">{label}</p>
      <p className="mt-1 font-sans text-2xl text-ink">{value}</p>
      {sub && <p className="mt-1 text-xs text-body">{sub}</p>}
    </div>
  );
}

/** A labelled count list with proportional bars — used for the three question-coverage
 * breakdowns, whose whole purpose is spotting which parts of the product are under-covered. */
function CountBars({ rows }: { rows: { label: string; count: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) return <p className="text-sm text-body">No questions yet.</p>;
  return (
    <ul className="flex flex-col gap-1">
      {rows.map((row) => (
        <li key={row.label} className="flex items-center gap-2 text-sm">
          <span className="w-44 shrink-0 truncate text-body" title={row.label}>
            {row.label}
          </span>
          <span className="h-2 flex-1 rounded bg-surface-alt">
            <span
              className="block h-2 rounded bg-brand"
              style={{ width: `${(row.count / max) * 100}%` }}
            />
          </span>
          <span className="w-8 shrink-0 text-right text-ink">{row.count}</span>
        </li>
      ))}
    </ul>
  );
}

/** T157 / FR-088: the CO Member's Home page — a platform-wide overview, filterable by company
 * and by the same app / App-Type / package taxonomy used everywhere else. Replaces the
 * `ComingSoon` placeholder that sat on this route since Phase 2. */
export function CoMemberHomePage() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [catalog, setCatalog] = useState<AppCatalog | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filters, setFilters] = useState<OverviewFilters>({});

  useEffect(() => {
    getAppCatalog().then(setCatalog).catch(() => setCatalog(null));
    listCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    getOverview(filters).then(setOverview).catch(() => setOverview(null));
  }, [filters]);

  if (!overview) return <div className="p-6 text-body">{t("common.loading", "Loading…")}</div>;

  const { testTakers } = overview;
  const completionPercent =
    testTakers.total > 0 ? Math.round((testTakers.completed / testTakers.total) * 100) : 0;

  // Only one of the three app filters applies at a time — they're different granularities of the
  // same axis, so combining them would be contradictory rather than narrowing.
  function setAppFilter(key: "app" | "appType" | "package", value: string) {
    setFilters((f) => ({ company: f.company, [key]: value || undefined }));
  }

  return (
    <div className="p-6">
      <h1 className="mb-4 font-sans text-xl text-ink">{t("nav.home", "Home")}</h1>

      <div className="mb-6 flex flex-wrap gap-3">
        <select
          aria-label="Filter by company"
          value={filters.company ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, company: e.target.value || undefined }))}
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        >
          <option value="">All companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select
          aria-label="Filter by app type"
          value={filters.appType ?? ""}
          onChange={(e) => setAppFilter("appType", e.target.value)}
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        >
          <option value="">All app types</option>
          {(catalog?.appTypes ?? []).map((t) => (
            <option key={t.appType} value={t.appType}>{t.appType}</option>
          ))}
        </select>

        <select
          aria-label="Filter by package"
          value={filters.package ?? ""}
          onChange={(e) => setAppFilter("package", e.target.value)}
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        >
          <option value="">All packages</option>
          {(catalog?.packages ?? []).map((p) => (
            <option key={p.code} value={p.code}>{p.label}</option>
          ))}
        </select>

        <select
          aria-label="Filter by app"
          value={filters.app ?? ""}
          onChange={(e) => setAppFilter("app", e.target.value)}
          className="rounded border border-surface-alt px-3 py-2 text-sm"
        >
          <option value="">All apps</option>
          {(catalog?.allApps ?? []).map((app) => (
            <option key={app} value={app}>{app}</option>
          ))}
        </select>

        {(filters.company || filters.app || filters.appType || filters.package) && (
          <button onClick={() => setFilters({})} className="text-sm text-brand">
            Clear filters
          </button>
        )}
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Companies" value={overview.companyCount} />
        <StatCard
          label="Questions"
          value={overview.questionCount.active}
          sub={`${overview.questionCount.inactive} deactivated`}
        />
        <StatCard
          label="Test takers completed"
          value={`${completionPercent}%`}
          sub={`${testTakers.completed} of ${testTakers.total}`}
        />
        <StatCard label="Still outstanding" value={testTakers.outstanding} />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded border border-surface-alt p-4">
          <h2 className="mb-3 font-sans text-sm text-ink">Questions by app type</h2>
          <CountBars rows={overview.questionsByAppType.map((r) => ({ label: r.appType, count: r.count }))} />
        </section>
        <section className="rounded border border-surface-alt p-4">
          <h2 className="mb-3 font-sans text-sm text-ink">Questions by package</h2>
          <CountBars rows={overview.questionsByPackage.map((r) => ({ label: r.label, count: r.count }))} />
        </section>
        <section className="rounded border border-surface-alt p-4">
          <h2 className="mb-3 font-sans text-sm text-ink">Questions by app</h2>
          <CountBars rows={overview.questionsByApp.map((r) => ({ label: r.app, count: r.count }))} />
        </section>
      </div>

      <section>
        <h2 className="mb-3 font-sans text-lg text-ink">Feedback</h2>
        {overview.feedback.choiceQuestions.length === 0 &&
        overview.feedback.freeTextQuestions.length === 0 ? (
          <p className="text-sm text-body">
            No feedback questions yet — mark a question as Feedback to collect responses here.
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            {overview.feedback.choiceQuestions.map((q) => (
              <div key={q.questionId} className="rounded border border-surface-alt p-4">
                <p className="mb-2 font-sans text-ink">{q.text}</p>
                <CountBars rows={q.options.map((o) => ({ label: `${o.option} (${o.percent}%)`, count: o.count }))} />
              </div>
            ))}
            {overview.feedback.freeTextQuestions.map((q) => (
              <div key={q.questionId} className="rounded border border-surface-alt p-4">
                <p className="mb-2 font-sans text-ink">{q.text}</p>
                {q.answers.length === 0 ? (
                  <p className="text-sm text-body">No responses yet.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {q.answers.map((a, i) => (
                      <li key={i} className="rounded bg-surface-alt px-3 py-2 text-sm text-body">
                        {a}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
